<script lang="ts">
  import { get } from 'svelte/store'
  import { grammarActions } from '$lib/stores/grammar'
  import { subscriptionStore } from '$lib/stores/subscription'
  import { grammarCheckService } from '$lib/services/GrammarCheckServiceV3'
  import { grammarCheckServiceMock } from '$lib/services/GrammarCheckServiceMock'
  import { undoRedoService } from '$lib/services/UndoRedoService'
  import { errorService } from '$lib/services/ErrorService'
  import { useError } from '$lib/hooks/useError'
  import type { BatchProgress } from '$lib/services/batchCheckService'
  import type { AppliedCorrectionInfo } from '$lib/services/UndoRedoService'
  
  /**
   * GrammarCheckController: Handles grammar checking operations
   * Single Responsibility: Grammar check execution and error management
   */
  
  interface Props {
    editorText: string
    errorState: any
    subscription: any
    hasChecked: boolean
    correctionStates: Map<string, AppliedCorrectionInfo>
    lastHistoryText: string | null
    onStatusMessage: (message: string) => void
    onBatchProgress: (progress: BatchProgress | null) => void
    onCheckComplete: () => void
    onCheckingStateChange?: (isChecking: boolean) => void
  }

  let {
    editorText = $bindable(''),
    errorState,
    subscription,
    hasChecked = $bindable(false),
    correctionStates,
    lastHistoryText,
    onStatusMessage,
    onBatchProgress,
    onCheckComplete,
    onCheckingStateChange = () => {}
  }: Props = $props()
  
  // Error handling setup
  const { tryExecute } = useError('GrammarCheck')
  
  // Feature flags for service selection
  const useMockService = import.meta.env.VITE_USE_MOCK_SERVICE === 'true' || false
  
  // Select appropriate service based on environment
  const getGrammarService = () => {
    if (useMockService) {
      return grammarCheckServiceMock
    }
    // Always use V3 for batch processing support
    return grammarCheckService // This is V3 with batch support
  }
  
  let isChecking = $state(false)
  let batchProgress = $state<BatchProgress | null>(null)
  
  export async function handleCheckGrammar() {
    // Don't check canPerformCheck here - let GrammarCheckService handle it
    // This allows the button to be clicked and show upgrade prompt
    
    isChecking = true
    onCheckingStateChange(true)
    batchProgress = null // Reset progress
    onBatchProgress(null)
    
    // Check if there's a selection
    const textarea = document.querySelector('textarea') as HTMLTextAreaElement
    let textToCheck = editorText
    let selectionInfo: { start: number; end: number; originalText: string } | null = null
    
    if (textarea && textarea.selectionStart !== textarea.selectionEnd) {
      // There is a selection - check only the selected text
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      textToCheck = editorText.substring(start, end)
      selectionInfo = { start, end, originalText: editorText }
      
      // Show status for selection check
      onStatusMessage('선택한 텍스트를 검사합니다...')
    } else {
      // Show status for full text check
      onStatusMessage('맞춤법 검사 중...')
    }
    
    // Select service based on feature flags
    const service = getGrammarService()
    
    const [result, error] = await tryExecute(
      async () => {
        // No need to show batch message here since the service handles it internally
        // The batch service will only trigger for texts > 4500 chars for Bareun/proxy
        
        // Pass progress callback for batch processing
        // Check if service has batch support (V3)
        let errors;
        if ('checkText' in service && service.checkText.length >= 2) {
          // V3 with batch support
          errors = await service.checkText(textToCheck, (progress: BatchProgress) => {
            batchProgress = progress
            onBatchProgress(progress)
            // Only show batch status message if there are multiple chunks
            // For Bareun: only shows when text > 4500 chars
            // For NaraSpeller: only shows when text > 500 chars
            if (progress.totalChunks > 1) {
              onStatusMessage(`긴 문서 처리 중: ${progress.currentChunk}/${progress.totalChunks} 부분 검사...`)
            }
            // Clear progress when completed
            if (progress.status === 'completed' || progress.status === 'failed') {
              setTimeout(() => {
                batchProgress = null
                onBatchProgress(null)
              }, 3000) // Keep visible for 3 seconds after completion
            }
          })
        } else {
          // V1/V2 without batch support
          errors = await service.checkText(textToCheck)
        }
        
        // If checking selection, adjust error positions
        if (selectionInfo) {
          errors = errors.map(error => ({
            ...error,
            position: error.position ? {
              start: error.position.start + selectionInfo.start,
              end: error.position.end + selectionInfo.start
            } : undefined,
            startPos: error.startPos !== undefined ? error.startPos + selectionInfo.start : undefined,
            endPos: error.endPos !== undefined ? error.endPos + selectionInfo.start : undefined
          }))
          
          // Restore selection after a brief delay to allow DOM update
          setTimeout(() => {
            if (textarea) {
              textarea.setSelectionRange(selectionInfo.start, selectionInfo.end)
              textarea.focus()
            }
          }, 10)
        }
        
        // 문법 검사 후에는 AI 오류와 병합해야 함 (기본값 true 사용)
        grammarActions.setErrors(errors)
        hasChecked = true
        
        // Add to history after grammar check if not already there
        if (editorText !== lastHistoryText) {
          undoRedoService.addEntry('edit', editorText, correctionStates, {
            description: '맞춤법 검사 완료',
            previousText: lastHistoryText || ''
          }, errors, true, errorState.ignoredErrors) // hasChecked = true after grammar check
        }
        
        // Record usage
        await subscriptionStore.recordUsage('spellCheck')
        
        // Also track to server for rate limiting
        const { usageTracker } = await import('$lib/services/UsageTrackingService')
        await usageTracker.trackGrammarCheck({
          textLength: editorText.length,
          errorCount: errors.length,
          responseTime: 0,
          source: 'editor'
        })
        
        // Get remaining uses for free users
        const remaining = subscriptionStore.getRemainingUses('spellCheck')
        const isFree = subscription.type === 'free'
        
        // Show error count message - only mention batch if actually processed in chunks
        const wasBatch = batchProgress && batchProgress.totalChunks > 1
        const isSelectionCheck = selectionInfo !== null
        
        if (errors.length > 0) {
          let message = isSelectionCheck
            ? `선택 영역에서 ${errors.length}개의 오류가 발견되었습니다`
            : wasBatch 
              ? `${errors.length}개의 오류가 발견되었습니다 (${batchProgress.totalChunks}개 부분으로 나누어 처리)`
              : `${errors.length}개의 오류가 발견되었습니다`
          if (isFree && remaining >= 0) {
            message += ` (남은 횟수: ${remaining}/10)`
          }
          onStatusMessage(message)
        } else {
          let message = isSelectionCheck
            ? '선택 영역에 문제가 없습니다'
            : wasBatch
              ? `문제가 없습니다 (${batchProgress.totalChunks}개 부분으로 나누어 처리)`
              : '문제가 없습니다'
          if (isFree && remaining >= 0) {
            message += ` (남은 횟수: ${remaining}/10)`
          }
          onStatusMessage(message)
        }
        
        onCheckComplete()
        return errors
      },
      {
        severity: 'error',
        context: { action: 'checkGrammar', textLength: editorText.length },
        showToUser: false, // We handle specific error display ourselves
        logToConsole: true
      }
    )
    
    if (error) {
      // Check for rate limit error (429)
      if (error.message?.includes('429') || error.message?.includes('Too Many Requests')) {
        // Check if we're in demo mode
        const { demoTracker } = await import('$lib/stores/demoStore')
        if (demoTracker.isDemoMode()) {
          // In demo mode, show demo expired modal instead
          // Mark as expired since we hit rate limit
          window.dispatchEvent(new CustomEvent('demo-expired', {
            detail: {
              feature: 'spellCheck',
              isRateLimited: true  // Force showing "expired" message
            }
          }))
        } else if (['premium', 'beta'].includes(get(subscriptionStore).type)) {
          onStatusMessage('서버 요청이 많습니다. 잠시 후 다시 시도해주세요.')
          errorService.showWarning('서버 요청이 많습니다. 잠시 후 다시 시도해주세요.')
        } else {
          // Show rate limit modal for regular users
          const { upgradePromptContext, showUpgradePrompt } = await import('$lib/stores/subscription')
          upgradePromptContext.set({
            reason: 'limit_reached',
            details: JSON.stringify({
              type: 'rate',
              used: 5, // Default to showing limit reached
              limit: 5,
              resetIn: 60 // 1 minute cooldown
            })
          })
          showUpgradePrompt.set(true)
        }
      }
      // Error already handled by GrammarCheckService (shows upgrade prompt)
      // But we can add additional handling if needed
      else if (!error.message?.includes('한도') && !error.message?.includes('구독')) {
        errorService.showWarning('문법 검사 중 오류가 발생했습니다. 다시 시도해주세요.')
      }
    }
    
    isChecking = false
    onCheckingStateChange(false)
  }

  export function getIsChecking() {
    return isChecking
  }

  export function getBatchProgress() {
    return batchProgress
  }

  export function resetCheckingState() {
    isChecking = false
    batchProgress = null
    onCheckingStateChange(false)
  }

  export function destroy() {
    grammarCheckService.destroy()
  }
</script>
